import { useState } from 'react'
import { GCC_EXCEL_GUIDE_ID } from '@shared/guides'
import { GCC_TAXONOMY_TEMPLATE_ID } from '@shared/templates'
import { useApi } from '../contexts/ApiContext'

type Action = 'guide' | 'template'

const FAILED: Record<Action, string> = {
  guide: 'The Excel guide could not be opened. Please try again.',
  template: 'The template could not be downloaded. Please try again.',
}

const LINK = 'text-xs font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-900 disabled:opacity-50'

/**
 * Help for the Step 1 Client Taxonomy upload (WI 2887): the Excel guide
 * (docs/gcc/gcc_excel_input_guide.html) and a template with its Roles and Tasks
 * sheets. Only clients that implement `openGuide` (web) show the guide link.
 */
export default function GccTaxonomyHelp() {
  const api = useApi()
  const [busy, setBusy] = useState<Action | null>(null)
  const [failed, setFailed] = useState<Action | null>(null)

  const run = async (action: Action, call: () => Promise<void>) => {
    setBusy(action)
    setFailed(null)
    try {
      await call()
    } catch {
      setFailed(action)
    } finally {
      setBusy(null)
    }
  }

  const openGuide = api.openGuide?.bind(api)

  return (
    <div className="mb-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {openGuide && (
          <button
            type="button"
            className={LINK}
            disabled={busy !== null}
            onClick={() => void run('guide', () => openGuide(GCC_EXCEL_GUIDE_ID))}
          >
            How to prepare your Excel file
          </button>
        )}
        <button
          type="button"
          className={LINK}
          disabled={busy !== null}
          onClick={() => void run('template', () => api.downloadTemplate(GCC_TAXONOMY_TEMPLATE_ID))}
        >
          {busy === 'template' ? 'Downloading…' : 'Download template'}
        </button>
      </div>
      {failed && <p role="alert" className="mt-1 text-xs text-red-600">{FAILED[failed]}</p>}
    </div>
  )
}
